import React from 'react';
import AuthContainer from '../../components/AuthContainer';
import ViewReverse from '../../components/ViewReverse';
import { ReactSVG } from 'react-svg';
import icons from '../../constants/icons';
import { Button, ConfirmModal, DatePicker, Dropdown, ErrorText, InputField, PhoneInput, Text } from '../../components/SecondaryMarket';
import CreateAccountFunctional from './index.function';
import { fontSizes } from '../../constants/constants';
import ViewItems from '../../components/ViewItems';
import dayjs from 'dayjs';
import CustomCheckbox from '../../components/CustomCheckbox';
import CustomToolTip from '../../components/ToolTip';
import { Formik } from 'formik';
import { create_account_schema, create_account_validation_schema } from '../../validations_schemas/create_account_validation_schema';
import { numeric_integer_regix } from '../../utils/functions';
import FullScreenLoader from '../../components/FullScreenLoader';

const CreateAccount = () => {
  const { TextWeight, isArabic, navigate, restrictModal, setRestrictModal, handleSubmit, handleDownloadTerms, applicationTypeDropdown, applicationTypeDropdownLoading, getContentKey, accountOpeningLoading, common_loading } = CreateAccountFunctional();

  return (
    <AuthContainer className={'w-full px-8 md:px-14 lg:px-24 py-6'}>
      <ViewReverse className={'items-center gap-3 cursor-pointer'} onClick={() => navigate(-1)}>
        <ReactSVG src={icons.back} />
        <Text text={getContentKey("back")} className={'text-text-grey mt-0.5'} />
      </ViewReverse>

      <Formik
        validationSchema={create_account_validation_schema}
        initialValues={create_account_schema}
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
        onSubmit={(values) => handleSubmit(values)}
        className='w-full'
      >
        {({ handleSubmit, setFieldValue, setFieldError, values, errors }) => {
          const isValid = values.entity_type && values.email && values.phone_number && values.unified_number && values.cr_expiry && values.is_terms_accepted;
          const disable = values?.entity_type?.id === 1066
          return (
            <>
              <Text text={getContentKey("create_new_account")} weight={TextWeight.headingRegular} className={'text-dark-blue mt-10 mb-9'} size={fontSizes.h30} />

              <Dropdown
                label={getContentKey("entity_type")}
                valueKey={'name'}
                value={applicationTypeDropdown?.applicantTypes?.find(item => item?.id === values?.entity_type?.id) ?? values?.entity_type}
                isRequired={false}
                placeholder={getContentKey("choose")}
                labelColor={'text-label'}
                options={applicationTypeDropdown?.applicantTypes}
                valueVeight={TextWeight.regular}
                onSelect={(val) => {
                  if (val?.id !== 1067) setRestrictModal(true);
                  setFieldValue('entity_type', val);
                }}
                menuClasses={'border-0 border-b px-0 rounded-none'}
                optionsClasses={'top-10'}
                singleOptionClass={'border-b-0'}
                error={errors?.entity_type}
              />

              {values?.entity_type?.id === 1067 && (
                <ViewItems className={'gap-8 mt-8'}>
                  <InputField
                    label={getContentKey("email_address")}
                    isRequired
                    isDisabled={disable}
                    placeholder={getContentKey("email_address")}
                    labelColor={'text-label'}
                    className={'border-0 border-b px-0 rounded-none'}
                    value={values?.email}
                    maxLength={50}
                    onChange={(text) => {
                      setFieldValue('email', text)
                      setFieldError('email', null)
                    }}
                    error={errors?.email}
                  />

                  <ViewItems className={'w-full'}>
                    <PhoneInput
                      label={getContentKey("phone_number")}
                      placeholder={getContentKey("phone_number_placeholder")}
                      labelColor={'text-label'}
                      className={'border-0 border-b px-0 rounded-none'}
                      value={values?.phone_number}
                      country={values?.country}
                      isDisabled={disable}
                      setCountry={(val) => setFieldValue('country', val)}
                      onChange={(text, isValid) => {
                        setFieldValue('phone_number', text);

                        if (!isValid) {
                          setFieldError('phone_number', getContentKey('invalid_phone_number'))
                        } else {
                          setFieldError('phone_number', null)
                        }
                      }}
                    />
                    <ErrorText text={errors?.phone_number} />
                  </ViewItems>

                  <ViewItems className={'w-full'}>
                    <ViewReverse className={'items-center gap-1 mb-1'}>
                      <Text text={getContentKey("unified_number")} isRequired size={fontSizes.h14} className={'text-label'} />
                      <CustomToolTip
                        title={getContentKey("unified_number_info")}
                      >
                        <span className='inline-flex mb-0.5'>
                          <ReactSVG src={icons.info_icon} />
                        </span>
                      </CustomToolTip>
                    </ViewReverse>
                    <InputField
                      isRequired
                      placeholder={'7xxxxxxxxx'}
                      labelColor={'text-label'}
                      isDisabled={disable}
                      maxLength={12}
                      className={'border-0 border-b px-0 rounded-none'}
                      value={values?.unified_number}
                      onChange={(text) => {
                        if (numeric_integer_regix.test(text)) {
                          setFieldValue('unified_number', text)
                          setFieldError('unified_number', null)
                        }
                      }}
                      error={errors?.unified_number}
                    />
                  </ViewItems>

                  <DatePicker
                    isRequired
                    label={getContentKey("cr_expiry_date")}
                    labelColor={'text-label'}
                    minDate={dayjs()}
                    className={'border-0 border-b px-0 rounded-none'}
                    selectedDate={values?.cr_expiry}
                    isDisabled={disable}
                    onChange={(date) => {
                      setFieldValue('cr_expiry', date?.$d)
                    }}
                    error={errors?.cr_expiry}
                  />

                  <ViewItems className={'w-full'}>
                    <CustomCheckbox
                      label={getContentKey("understand_and_agree")}
                      secondText={getContentKey("terms_and_conditions")}
                      size={fontSizes.h13}
                      weight={TextWeight.regular}
                      color={'text-black'}
                      secondSize={fontSizes.h13}
                      className={'mt-3'}
                      isChecked={values?.is_terms_accepted}
                      onClick={() => setFieldValue('is_terms_accepted', !values?.is_terms_accepted)}
                      onPressSecondText={() => handleDownloadTerms(1)}
                      connectorText={getContentKey("and")}
                      thirdText={getContentKey("privacy_policy")}
                      onPressThirdText={() => handleDownloadTerms(2)}
                    />
                    <ErrorText text={errors?.is_terms_accepted} />
                  </ViewItems>

                  <Button text={getContentKey("next")} className={'w-full'} loading={accountOpeningLoading} isDisabled={!isValid || accountOpeningLoading} onClick={() => handleSubmit(values)} />

                  <Text
                    text={
                      <>
                        {getContentKey('already_have_access')}{' '}
                        <Text text={getContentKey('login')} className={'text-dark-blue cursor-pointer inline'} align={'text-center'} onClick={() => window.open(process.env.REACT_APP_CLIENT_PORTAL_LINK + (isArabic ? 2 : 1), '_self')} />
                      </>
                    }
                    className={'w-full'}
                    align={'text-center'}
                  />
                </ViewItems>
              )}
              <ConfirmModal
                isOpen={restrictModal}
                title={getContentKey("access_restricted")}
                descOne={getContentKey("contact_jadwa_service_center")}
                descTwo={'servicecenter@jadwa.com'}
                descTwoClasses={'text-light-blue cursor-pointer underline'}
                icon={icons.info}
                leftBtn={getContentKey("dismiss")}
                onDescPress={() => window.open('mailto:servicecenter@jadwa.com', '_blank')}
                onClose={() => setRestrictModal(false)}
                onLeftPress={() => setRestrictModal(false)}
              />
            </>
          );
        }}
      </Formik>

      {(applicationTypeDropdownLoading || common_loading) && <FullScreenLoader />}
    </AuthContainer>
  );
};

export default CreateAccount;
